import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import { Check, ArrowRight, Sparkles, BarChart3, UserCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const GROUPS = [
  {
    id: 'ai',
    icon: Sparkles,
    image: {
      en: '/home/section-4/ai-powered-tools.webp',
      ar: '/home/section-4/ai-powered-tools-ar.webp',
    },
    title: { en: 'AI-Powered Tools', ar: 'أدوات مدعومة بالذكاء الاصطناعي' },
    description: {
      en: 'Create faster. Automate smarter.',
      ar: 'أتمت المهام المتكررة، ووفّر الوقت، ونجز أعمالك بكفاءة أكبر',
    },
    items: {
      en: [
        'Alpha',
        'Leo',
        'Emma',
        'Bekam',
        'E7sebly',
        'Sales Leads',
        'Connecto Studio',
      ],
      ar: [
        'كونكتو ستوديو | إنشاء صفحات منتجات متكاملة',
        'ألفا | إنشاء صور احترافية للمنتجات',
        'ليو | كتابة محتوى احترافي متوافق مع محركات البحث',
        'إيما | مساعد ذكي للإجابة على استفسارات البائعين',
        'بكام | متابعة أسعار المنافسين وتحليل تغيراتها',
        'احسبلي | حساب التكاليف، والرسوم، وصافي الأرباح',
      ],
    },
  },
  {
    id: 'business',
    icon: BarChart3,
    image: {
      en: '/home/section-4/business-management.webp',
      ar: '/home/section-4/business-management-ar.webp',
    },
    title: { en: 'Business Management', ar: 'أدوات إدارة المنتجات وتحليل الأداء' },
    description: {
      en: 'Monitor, analyze, and simplify your daily operations.',
      ar: 'تابع أداء أعمالك واتخذ قرارات مبنية على البيانات',
    },
    items: {
      en: [
        'Dashboard Analytics',
        'QR & Barcode Generator',

      ],
      ar: [
        'لوحة تحكم كونكتو | متابعة المبيعات، والمخزون، والأداء',
        'QR & Barcode | إنشاء أكواد QR والباركود',
        'مدقق | تقدير الضرائب والتخطيط المالي',
      ],
    },
  },
  {
    id: 'services',
    icon: UserCheck,
    image: {
      en: '/home/section-4/expert-marketplace-services.webp',
      ar: '/home/section-4/expert-marketplace-services-ar.webp',
    },
    title: { en: 'Expert Marketplace Services', ar: 'خدمات يقدمها خبراء التجارة الإلكترونية' },
    description: {
      en: 'Work with marketplace specialists to accelerate your growth.',
      ar: 'استفد من خبرات متخصصة لمساعدتك على النمو والتوسع.',
    },
    items: {
      en: [
        'Tax Auditor',
        'Vendor Market',
        'Book Training',
        'Book a Consultant',

      ],
      ar: [
        'احجز تدريب | جلسات تدريب متخصصة',
        'سوق الموردين | للوصول إلى موردين موثوقين',
        'فرص البيع | لاكتشاف فرص البيع الأفضل في المنصات',
        'احجز خبير | حجز استشارة مع خبراء التجارة الإلكترونية',
        'بيع معانا | للتوسع في البيع على المنصات',
      ],
    },
  },
];

const Solutions = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const locale = language === 'ar' ? 'ar' : 'en';
  const isAr = locale === 'ar';

  const [activeId, setActiveId] = useState('ai');
  const activeGroup = GROUPS.find((group) => group.id === activeId) || GROUPS[0];

  return (
    <section className="bg-surface-section py-14 md:py-20 lg:py-24 border-t border-b border-edge-card" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.55 }}
          className="text-center max-w-4xl lg:max-w-6xl mx-auto mb-10 md:mb-14"
        >
          <span className="inline-flex items-center justify-center px-6 py-2 rounded-full bg-badge text-primary-dark text-sm sm:text-base font-bold font-almarai mb-4">
            {isAr ? 'حلولنا الذكية' : 'Our Smart Solutions'}
          </span>
          <h2 className="font-ibm text-[26px] md:text-[34px] font-bold text-heading leading-tight lg:whitespace-nowrap">
            {t.solutionsMainTitle}
          </h2>
        </motion.div>

        {/* Tab Navigation */}
        <div className="mb-10 border-y border-edge-card md:mb-14">
          <div className="flex overflow-x-auto justify-start md:justify-center no-scrollbar" role="tablist">
            {GROUPS.map((group) => {
              const active = group.id === activeId;
              const Icon = group.icon;
              return (
                <button
                  key={group.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveId(group.id)}
                  className={`relative min-w-fit flex-1 whitespace-nowrap px-5 py-4 md:py-5 font-ibm text-sm md:text-base font-semibold transition-colors inline-flex items-center justify-center gap-2.5 ${
                    active ? 'text-primary' : 'text-body-muted hover:text-primary'
                  }`}
                >
                  <Icon className={`w-4 h-4 md:w-5 md:h-5 ${active ? 'text-primary' : 'text-body-muted'}`} />
                  <span>{group.title[locale]}</span>
                  {active && (
                    <motion.span
                      layoutId="solutions-tab-underline"
                      className="absolute inset-x-4 bottom-0 h-[3px] rounded-t-full bg-accent-orange"
                      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeGroup.id}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="grid items-center gap-8 lg:grid-cols-2 lg:gap-16 max-w-6xl mx-auto"
          >
            <div className="py-2 text-start">
              <h3 className="font-ibm text-[22px] md:text-[28px] font-bold text-heading mb-3 flex items-center gap-2.5">
                {React.createElement(activeGroup.icon, { className: 'w-6 h-6 text-primary' })}
                <span>{activeGroup.title[locale]}</span>
              </h3>
              <p className="font-almarai text-[15px] md:text-[17px] text-body-muted leading-relaxed mb-6">
                {activeGroup.description[locale]}
              </p>

              <ul className="grid gap-x-6 gap-y-3 sm:grid-cols-2 mb-8">
                {activeGroup.items[locale].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-start font-almarai text-[14px] md:text-[15px] text-heading">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-orange-bg text-accent-orange">
                      <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/service/platform/"
                className="inline-flex items-center gap-2 rounded-xl bg-dark hover:bg-dark-hover px-6 py-3.5 font-almarai text-sm font-bold text-white transition-all shadow-md hover:-translate-y-0.5"
              >
                <span>{isAr ? 'عرض المزيد' : 'View More'}</span>
                <ArrowRight className={`h-4 w-4 ${isAr ? 'rotate-180' : ''}`} />
              </Link>
            </div>

            <div className="overflow-hidden rounded-[24px] bg-white shadow-card-soft border border-edge-card">
              <img
                loading="lazy"
                width="1448"
                height="1086"
                src={typeof activeGroup.image === 'object' ? (activeGroup.image[locale] || activeGroup.image.en) : activeGroup.image}
                alt={activeGroup.title[locale]}
                className="block aspect-[4/3] h-auto w-full object-cover"
              />
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Solutions;